import React, { useMemo, useState } from 'react';
import Badge from '../../../components/ui/Badge';
import { ChevronDownIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReferenceDataGeneralList, type ReferenceInboxColumn } from '../ReferenceDataGeneralList';
import { moduleConfig } from './config';
import { useCrewHolidays, type CrewHoliday } from './useData';

const KIND_LABEL: Record<string, string> = {
  REGULAR: 'Regular',
  SPECIAL_NON_WORKING: 'Special',
};

const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const formatDate = moduleConfig.utils.formatDate;
  const [kind, setKind] = useState('');
  const yearNow = new Date().getFullYear();
  const [year, setYear] = useState(String(yearNow));

  const columns = useMemo<ReferenceInboxColumn<CrewHoliday>[]>(
    () => [
      {
        key: 'sortOrder',
        header: 'Rule',
        className: 'min-w-[10rem]',
        render: (row) => (
          <div>
            <div className={cn('text-sm font-medium', colors.text)}>{row.ruleLabel || '—'}</div>
            <div className={cn('text-xs tabular-nums', colors.textMuted)}>
              {row.resolvedDate
                ? `${formatDate(row.resolvedDate)} · ${row.resolvedYear ?? year}`
                : `No date in ${row.resolvedYear ?? year}`}
            </div>
          </div>
        ),
      },
      {
        key: 'code',
        header: 'Code',
        className: 'whitespace-nowrap',
        render: (row) => (
          <span className={cn('font-medium font-mono text-xs tracking-wide', colors.text)}>
            {row.code}
          </span>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        render: (row) => <span className={cn('font-medium', colors.text)}>{row.name}</span>,
      },
      {
        key: 'kind',
        header: 'Kind',
        className: 'hidden whitespace-nowrap sm:table-cell',
        render: (row) => (
          <Badge variant={row.kind === 'REGULAR' ? 'success' : 'secondary'}>
            {KIND_LABEL[row.kind] ?? row.kind}
          </Badge>
        ),
      },
      {
        key: 'countryCode',
        header: 'Country',
        className: 'hidden whitespace-nowrap md:table-cell',
        render: (row) => <span className={colors.textMuted}>{row.countryCode}</span>,
      },
      {
        key: 'isActive',
        header: 'Active',
        className: 'whitespace-nowrap',
        render: (row) => (
          <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
        ),
      },
    ],
    [colors.text, colors.textMuted, formatDate, year],
  );

  const extraFilters = useMemo(() => {
    const f: Record<string, string[]> = {};
    if (kind) f.kind = [kind];
    if (year) f.year = [year];
    return Object.keys(f).length ? f : undefined;
  }, [kind, year]);

  const yearOptions = [yearNow - 1, yearNow, yearNow + 1, yearNow + 2].map(String);

  return (
    <ReferenceDataGeneralList
      moduleConfig={moduleConfig}
      useData={useCrewHolidays}
      columns={columns}
      defaultSort={{ key: 'sortOrder', direction: 'asc' }}
      newLabel="New holiday"
      getRowLabel={(row) => row.name}
      loadingLabel="Loading holidays…"
      extraFilters={extraFilters}
      hasExtraFilters={Boolean(kind || (year && year !== String(yearNow)))}
      onClearExtraFilters={() => {
        setKind('');
        setYear(String(yearNow));
      }}
      tabTrailing={
        <>
          <span
            className={cn(
              'mx-1 my-1.5 w-px shrink-0 self-stretch',
              isDark ? 'bg-white/15' : 'bg-slate-300/80',
            )}
            aria-hidden
          />
          <label
            className={cn(
              'relative inline-flex items-center rounded-lg transition',
              year
                ? cn(colors.surface, 'shadow-sm ring-1', isDark ? 'ring-white/10' : 'ring-black/5')
                : colors.interactiveHover,
            )}
          >
            <span className="sr-only">Preview year</span>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className={cn(
                'appearance-none cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-3 pr-8 text-sm font-medium outline-none',
                'focus:ring-2 focus:ring-indigo-500/25',
                year ? colors.text : colors.textMuted,
              )}
              title="Preview dates for year"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              className={cn(
                'pointer-events-none absolute right-2 h-3.5 w-3.5',
                year ? colors.primaryFg : 'opacity-50',
              )}
            />
          </label>
          <label
            className={cn(
              'relative inline-flex items-center rounded-lg transition',
              kind
                ? cn(colors.surface, 'shadow-sm ring-1', isDark ? 'ring-white/10' : 'ring-black/5')
                : colors.interactiveHover,
            )}
          >
            <span className="sr-only">Filter by kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className={cn(
                'appearance-none cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-3 pr-8 text-sm font-medium outline-none',
                'focus:ring-2 focus:ring-indigo-500/25',
                kind ? colors.text : colors.textMuted,
              )}
              title="Filter by kind"
            >
              <option value="">All kinds</option>
              <option value="REGULAR">Regular</option>
              <option value="SPECIAL_NON_WORKING">Special non-working</option>
            </select>
            <ChevronDownIcon
              className={cn(
                'pointer-events-none absolute right-2 h-3.5 w-3.5',
                kind ? colors.primaryFg : 'opacity-50',
              )}
            />
          </label>
        </>
      }
    />
  );
};

export default List;
